import { lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AppLayout } from './layout/AppShell';
import { AuthProvider } from './auth/AuthContext';
import { RequireAuth } from './auth/RequireAuth';
import { ErrorBoundary } from './components/ErrorBoundary';

const Dashboard = lazy(() => import('./pages/Dashboard').then(m => ({ default: m.Dashboard })));
const Chat = lazy(() => import('./pages/Chat').then(m => ({ default: m.Chat })));
const Email = lazy(() => import('./pages/Email').then(m => ({ default: m.Email })));
const Agents = lazy(() => import('./pages/Agents').then(m => ({ default: m.Agents })));
const Contacts = lazy(() => import('./pages/Contacts').then(m => ({ default: m.Contacts })));
const LegalRegister = lazy(() => import('./pages/LegalRegister').then(m => ({ default: m.LegalRegister })));
const Usage = lazy(() => import('./pages/Usage').then(m => ({ default: m.Usage })));
const Subscriptions = lazy(() => import('./pages/Subscriptions').then(m => ({ default: m.Subscriptions })));
const CashFlow = lazy(() => import('./pages/CashFlow').then(m => ({ default: m.CashFlow })));
const BodyCorp = lazy(() => import('./pages/BodyCorp').then(m => ({ default: m.BodyCorp })));
const Tenancy = lazy(() => import('./pages/Tenancy').then(m => ({ default: m.Tenancy })));
const EmailTimeline = lazy(() => import('./pages/EmailTimeline').then(m => ({ default: m.EmailTimeline })));
const WorkProgress = lazy(() => import('./pages/WorkProgress').then(m => ({ default: m.WorkProgress })));
// Knowledge + TimelineViewer pages removed from nav 2026-05-15 (not working).
// Imports kept commented for fast restore if needed.
// const TimelineViewer = lazy(() => import('./pages/TimelineViewer').then(m => ({ default: m.TimelineViewer })));
// const Knowledge = lazy(() => import('./pages/Knowledge').then(m => ({ default: m.Knowledge })));
const Automations = lazy(() => import('./pages/Automations').then(m => ({ default: m.Automations })));
const Issues = lazy(() => import('./pages/Issues').then(m => ({ default: m.Issues })));
const IssueDetail = lazy(() => import('./pages/IssueDetail').then(m => ({ default: m.IssueDetail })));
const MonitoringPage = lazy(() => import('./pages/Monitoring').then(m => ({ default: m.Monitoring })));
// JBT Marel Tracker removed 2026-10-21 (inbox access ended). Data archived on
// the NAS; the Dashboard took back the index route.

/** Wrap each page route in an error boundary so a crash in one page doesn't take down the app */
function Page({ children, label }: { children: React.ReactNode; label: string }) {
  return <ErrorBoundary label={label}>{children}</ErrorBoundary>;
}

export function App() {
  return (
    <AuthProvider>
      <Routes>
        <Route element={<RequireAuth><AppLayout /></RequireAuth>}>
          {/* Old /dashboard and /jbt bookmarks redirect home. */}
          <Route index element={<Page label="Dashboard"><Dashboard /></Page>} />
          <Route path="dashboard" element={<Navigate to="/" replace />} />
          <Route path="chat" element={<Page label="Chat"><Chat /></Page>} />
          <Route path="email" element={<Page label="Email"><Email /></Page>} />
          <Route path="agents" element={<Page label="Agents"><Agents /></Page>} />
          <Route path="contacts" element={<Page label="Contacts"><Contacts /></Page>} />
          <Route path="legal" element={<Page label="Legal Register"><LegalRegister /></Page>} />
          <Route path="usage" element={<Page label="Usage"><Usage /></Page>} />
          <Route path="cashflow" element={<Page label="Cash Flow"><CashFlow /></Page>} />
          <Route path="subscriptions" element={<Page label="Subscriptions"><Subscriptions /></Page>} />
          <Route path="argyle" element={<Navigate to="/bodycorp" replace />} />
          <Route path="bodycorp" element={<Page label="Body Corp"><BodyCorp /></Page>} />
          <Route path="tenancy" element={<Page label="Tenancy"><Tenancy /></Page>} />
          <Route path="email-timeline" element={<Page label="Email Timeline"><EmailTimeline /></Page>} />
          <Route path="work-progress" element={<Page label="Work Progress"><WorkProgress /></Page>} />
          {/* Knowledge + Timeline routes removed 2026-05-15 — pages not working */}
          <Route path="automations" element={<Page label="Automations"><Automations /></Page>} />
          <Route path="issues" element={<Page label="Issues"><Issues /></Page>} />
          <Route path="issues/:ticketId" element={<Page label="Issue Detail"><IssueDetail /></Page>} />
          <Route path="monitoring" element={<Page label="Monitoring"><MonitoringPage /></Page>} />
          <Route path="jbt" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </AuthProvider>
  );
}
